import { Row, Col, Button, Flex, message, Drawer, Spin, Avatar, Modal, Input, Select, DatePicker, Menu, Badge, Tooltip } from "antd";
import React, { FC, useEffect, useState } from "react";
import { IApplicant, IJob, IProjectDetails, IScheduleCall, recruiterVisibleStatuses } from "../helper";
import ApplicantsList from "./applicantsList";
import { axiosInstant, endpoints } from "../../../api";
import { showError } from "../../../Notifications";
import RemarksPanel from "../../../Components/RemarksPanel";
import { FilePdfOutlined, LinkedinOutlined, EyeOutlined, MessageOutlined, CommentOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useUser } from "src/context/auth.context";
import ConfettiCelebration from "../../common/ConfettiCelebration";
import { useChatUnreadCounts } from "../../common/useChatUnreadCounts";

interface Props {
  job?: IJob;
  scheduleCall: IScheduleCall;
}
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const getResumeUrl = (resume: string) => {
  if (!resume) return "";
  if (resume.startsWith("http")) return resume;
  return `${API_BASE_URL}${resume}`;
};

const getProfilePhotoUrl = (profilePhoto: string) => {
  if (!profilePhoto) return "";
  if (profilePhoto.startsWith("http")) return profilePhoto;
  return `${API_BASE_URL}${profilePhoto}`;
};

function renderFeedback(text: string | undefined) {
  if (!text) return <span>—</span>;
  const lines = text.split(/\n/);
  const elements: JSX.Element[] = [];
  let bullets: string[] = [];
  lines.forEach((line: string, idx: number) => {
    const trimmed = line.trim();
    if (/^[-•*]\s+/.test(trimmed)) {
      bullets.push(trimmed.replace(/^[-•*]\s+/, ""));
    } else {
      if (bullets.length) {
        elements.push(
          <ul key={`ul-fb-${idx}`}>
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        );
        bullets = [];
      }
      if (trimmed) {
        elements.push(<p key={`p-fb-${idx}`}>{trimmed}</p>);
      }
    }
  });
  if (bullets.length) {
    elements.push(
      <ul key={`ul-fb-last`}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
  }
  return elements;
}

export const ApplicantsListPanel: FC<Props> = ({ job, scheduleCall }) => {
  const [jobApplications, setJobApplications] = useState<Array<IApplicant>>([]);
  const [selectedCandidate, setSelectedCandidate] = useState("");
  const [loading, setLoading] = useState(false);
  const [notesModal, setNotesModal] = useState<{ visible: boolean; notes: string }>({ visible: false, notes: "" });
  const [feedbackModal, setFeedbackModal] = useState<{ visible: boolean; applicationId?: string; feedback?: string }>({ visible: false });
  const [feedbackValue, setFeedbackValue] = useState("");
  const [feedbackLocked, setFeedbackLocked] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerApplicant, setDrawerApplicant] = useState<IApplicant | null>(null);
  const [drawerProfile, setDrawerProfile] = useState<any>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [talentPoolModal, setTalentPoolModal] = useState<{ visible: boolean; candidateId?: string; managerFeedback?: string; note: string; saving: boolean; loadingNote: boolean }>({ visible: false, note: '', saving: false, loadingNote: false });
  const [remarksPanel, setRemarksPanel] = useState<{ visible: boolean; jobApplicationId?: string; managerId?: string }>({ visible: false });
  const [selectedJobApplicationId, setSelectedJobApplicationId] = useState("");
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [offerModal, setOfferModal] = useState<{ visible: boolean, record?: IApplicant }>({ visible: false });
  const [offerFields, setOfferFields] = useState({
    positionTitle: "",
    companyName: "",
    employmentType: "Full-time",
    salary: "",
    location: "",
    startDate: null,
    notes: "",
    offerLetter: "",
  });
  const [generating, setGenerating] = useState(false);
  const [viewOfferModal, setViewOfferModal] = useState<{ visible: boolean, record?: IApplicant }>({ visible: false });
  // Unread messages in the chat with the hiring manager, per application.
  const unreadCount = useChatUnreadCounts('recruiter-manager');
  const [showConfetti, setShowConfetti] = useState(false);
  const [recruiterRejectModal, setRecruiterRejectModal] = useState<{ visible: boolean; applicationId?: string }>({ visible: false });
  const [recruiterRejectReason, setRecruiterRejectReason] = useState("");
  const [recruiterRejectLoading, setRecruiterRejectLoading] = useState(false);
  const [sendRejectionModal, setSendRejectionModal] = useState<{ visible: boolean; applicationId?: string; candidateName?: string }>({ visible: false });
  const [sendRejectionMessage, setSendRejectionMessage] = useState("");
  const [sendRejectionLoading, setSendRejectionLoading] = useState(false);
  const [readFeedbackIds, setReadFeedbackIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('cf_feedback_read');
      const arr: string[] = stored ? JSON.parse(stored) : [];
      return new Set<string>(arr);
    } catch { return new Set<string>(); }
  });
  const { user } = useUser();

  const updateApplicantsArray = (newApplicants: Array<IApplicant>) => {
    const freshApplicants = newApplicants.filter(
      (applicant) => !jobApplications.find((e) => e._id === applicant._id)
    );
    setJobApplications((prev) => [...prev, ...freshApplicants]);
  };
  // Menu logic is now handled in misc.tsx via ApplicantsColumns
  
  const fetchApplications = async () => {
    if (!job?._id) return;
    setLoading(true);
    try {
      const res = await axiosInstant.get(`/job-applications/applications/by-job/${job?._id}`);

      const data = res.data;
      const visible = (Array.isArray(data) ? data : []).filter(app => recruiterVisibleStatuses.includes(app.status));
      const mapped = visible.map(app => ({
        _id: app._id,
        name: app.profile?.firstName && app.profile?.lastName
          ? `${app.profile.firstName} ${app.profile.lastName}`
          : app.profile?.firstName || app.profile?.lastName || app.candidate?.name || "",
        email: app.profile?.email || app.candidate?.email || "",
        position: app.positionTitle || app.position || "",
        resume: app.profile?.resume || app.resume || "",
        notes: app.notes || "",
        hiddenFields: app.hiddenFields || [],
        feedback: app.feedback || "",
        candidateFeedback: app.candidateFeedback || "",
        profilePicture: app.profile?.profilePictureUrl || app.candidate?.profilePictureUrl || "",
        linkedin: app.profile?.linkedin || app.candidate?.linkedin || "",
        remarks: app.remarks || [],
        status: app.status,
        applicantId: app.candidateId || app.profile?.userId,
        profile: app.profile,
        jobApplicationId: app._id,
        secondInterviewDate: app.secondInterviewDate,
        secondInterviewLink: app.secondInterviewLink,
        secondInterviewType: app.secondInterviewType,
        isEmailSent: app.isEmailSent ?? false,
        location: app.profile?.location || app.candidate?.location || "",
        candidateId: app.candidate?._id || app.candidateId || app.profile?.userId,
        managerFeedback: app.managerFeedback || app.feedback || "",
        offerLetter: app.offerLetter || "",
        offerAccepted: app.offerAccepted || false,
        offerRejected: app.offerRejected || false,
        offerAcceptedDate: app.offerAcceptedDate || "",
        offerRejectedDate: app.offerRejectedDate || "",
        offerAcceptedBy: app.offerAcceptedBy || "",
        offerRejectedBy: app.offerRejectedBy || "",
        screeningScore: app.screeningScore,
        screeningDetails: app.screeningDetails,
        screeningDate: app.screeningDate,
        isScreened: app.isScreened || false,
      }));
      console.log('Raw applications from backend:', data);
      console.log('Mapped applicants:', mapped);
      setJobApplications(mapped);
    } catch {
      message.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!job?._id) return;
    setLoading(true);
    fetchApplications();
  }, [job?._id]);

  const handleShortlist = (applicationId: string) => {
    // Placeholder for shortlist API call
    message.success('Candidate shortlisted!');
  };

  const handleReject = (applicationId: string) => {
    setFeedbackModal({ visible: true, applicationId });
  };

  const handleSubmitFeedback = () => {
    // Placeholder for reject API call with feedback
    setFeedbackModal({ visible: false, applicationId: '' });
    setFeedbackValue('');
    message.success('Feedback submitted and candidate rejected!');
  };      
  const sendEmailRequest = async (_id: string, emailBody: string, subject: string) => {
    try {
      const applicant = jobApplications.find(app => app._id === _id);
      if (!applicant?.applicantId) {
        throw new Error('User ID not found');
      }
      await axiosInstant.post(endpoints.emailRequest, {
        jobId: job?._id,
        jobApplicationId: applicant.jobApplicationId,
        userId: applicant.applicantId,
        emailBody,
        subject,
      });
      message.success('Email sent successfully!');
      fetchApplications();
    } catch (e) {
      showError(e);
    }
  };

  const fetchProfileById = async (_id: string) => {
    setDrawerLoading(true);
    try {
      const res = await axiosInstant.get(`/candidates/${_id}`);
      console.log("Drawer profile data:", res.data);
      setDrawerProfile(res.data);
    } catch (e) {
      showError('Failed to fetch profile details');
      setDrawerProfile(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const openMoreInfo = (record: any) => {
    setDrawerProfile(record);
    setDrawerVisible(true);
   
  };

  const openTalentPool = async (applicant: IApplicant) => {
    // Mark feedback as read
    setReadFeedbackIds(prev => {
      const next = new Set(prev);
      next.add(applicant._id);
      try { localStorage.setItem('cf_feedback_read', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
    setTalentPoolModal({ visible: true, candidateId: applicant.candidateId, managerFeedback: applicant.managerFeedback, note: '', saving: false, loadingNote: true });
    try {
      const res = await axiosInstant.get(`/jobs/candidate-pool/note-for-user/${applicant.candidateId}`);
      setTalentPoolModal(prev => ({ ...prev, note: res.data?.note || '', loadingNote: false }));
    } catch {
      setTalentPoolModal(prev => ({ ...prev, loadingNote: false }));
    }
  };

  const openRemarks = (jobApplicationId: string) => {
    setRemarksPanel({ visible: true, jobApplicationId });
  };

  const openNotes = (notes: string) => setNotesModal({ visible: true, notes });
  const openFeedback = (applicationId: string, feedback: string) => {
    setFeedbackModal({ visible: true, applicationId, feedback });
    setFeedbackValue(feedback);
    setFeedbackLocked(!!feedback);
  };

  const markAsHired = async (applicationId: string) => {
    console.log('🎯 markAsHired called with applicationId:', applicationId);
    try {
      console.log('📡 Making API call to update status to hired...');
      const response = await axiosInstant.patch(`/job-applications/applications/${applicationId}/status`, {
        status: 'hired'
      });
      console.log('✅ API Response:', response.data);
      
      // Show confetti celebration
      console.log('🎊 Setting confetti to true');
      setShowConfetti(true);
      
      // Show success message
      message.success("🎉 Candidate has been hired! Congratulations!");
      
      // Refresh applications
      console.log('🔄 Refreshing applications...');
      await fetchApplications();
    } catch (error) {
      console.error('❌ Error marking as hired:', error);
      message.error("Failed to mark candidate as hired");
    }
  };

  const openRecruiterReject = (applicationId: string) => {
    setRecruiterRejectReason("");
    setRecruiterRejectModal({ visible: true, applicationId });
  };

  const openSendRejection = (record: IApplicant) => {
    setSendRejectionMessage(record.managerFeedback || "");
    setSendRejectionModal({ visible: true, applicationId: record._id, candidateName: record.name });
  };

  const handleSendRejection = async () => {
    if (!sendRejectionModal.applicationId) return;
    setSendRejectionLoading(true);
    try {
      await axiosInstant.post(`/job-applications/applications/${sendRejectionModal.applicationId}/send-final-rejection`, {
        message: sendRejectionMessage.trim(),
      });
      message.success('Rejection email sent to candidate.');
      setSendRejectionModal({ visible: false });
      fetchApplications();
    } catch {
      message.error('Failed to send rejection email.');
    } finally {
      setSendRejectionLoading(false);
    }
  };

  const handleRecruiterRejectConfirm = async () => {
    if (!recruiterRejectModal.applicationId) return;
    setRecruiterRejectLoading(true);
    try {
      await axiosInstant.patch(`/job-applications/applications/${recruiterRejectModal.applicationId}/status`, {
        status: 'rejected_recruiter',
        notes: recruiterRejectReason.trim(),
      });
      message.success('Candidate rejected.');
      setRecruiterRejectModal({ visible: false });
      fetchApplications();
    } catch {
      message.error('Failed to reject candidate');
    } finally {
      setRecruiterRejectLoading(false);
    }
  };

  const openOfferModal = (record: IApplicant) => {
    setOfferFields({
      positionTitle: record.position || job?.positionTitle || "",
      companyName: job?.companyName || "",
      employmentType: "Full-time",
      salary: job?.salary || "",
      location: job?.location || "",
      startDate: null,
      notes: "",
      offerLetter: "",
    });
    setOfferModal({ visible: true, record });
  };


  const canShare = offerFields.positionTitle && offerFields.companyName && offerFields.salary && offerFields.location && offerFields.offerLetter;
  const shareOfferWithCandidate = async () => {
    if (!offerModal.record?._id) return;
    try {
      await axiosInstant.patch(
        `/job-applications/applications/${offerModal.record._id}/status`,
        {
          status: 'offer_placed',
          offerAmount: offerFields.salary,
          offerLetter: offerFields.offerLetter,
          offerNotes: offerFields.notes,
          offerDate: new Date(),
          // Add any other fields you want to save
        }
      );
      setOfferModal({ visible: false });
      setOfferFields({
        positionTitle: "",
        companyName: "",
        employmentType: "Full-time",
        salary: "",
        location: "",
        startDate: null,
        notes: "",
        offerLetter: "",
      });
      fetchApplications();
      message.success("Offer shared with candidate!");
    } catch (err) {
      message.error("Failed to share offer with candidate.");
    }
  };

  const generateOfferLetter = async () => {
    setGenerating(true);
    try {
      const { data } = await axiosInstant.post(
        '/job-applications/ai/generate-offer-letter',
        {
          ...offerFields,
          candidateName: offerModal.record?.name,
        }
      );
      setOfferFields(fields => ({ ...fields, offerLetter: data.letter }));
      if (offerModal.record?._id) {
        await axiosInstant.patch(`/job-applications/applications/${offerModal.record._id}/share-offer`, {
          offerLetter: data.letter,
        });
      }
    } catch (err) {
      message.error('Failed to generate offer letter');
    } finally {
      setGenerating(false);
    }
  };

  const props = {
    applicants: jobApplications,
    job,
    jobId: job?._id,
    openRemarks: openRemarks,
    remarksUnreadCounts: unreadCount,
    sendEmailRequest,
    scheduleCall,
    openMoreInfo,
    openProfileDrawer: (profile: any) => openMoreInfo(profile),
    refetch: fetchApplications,
    openTalentPool,
    openNotes: (notes: string) => openNotes(notes),
    openFeedback: (id: string, feedback: string) => openFeedback(id, feedback),
    markAsHired: (applicationId: string) => markAsHired(applicationId),
    openOfferModal,
    setOfferModal,
    setViewOfferModal,
    openRecruiterReject,
    openSendRejection,
  };

  return (
    <>
      {job && (
        <ApplicantsList
          {...props}
          jobId={job?._id}
          project={{ ...job, name: job.companyName } as IProjectDetails }
          refetch={fetchApplications}
          openProfileDrawer={openMoreInfo}
          openTalentPool={openTalentPool}
          openOfferModal={openOfferModal}
          setOfferModal={setOfferModal}
          setViewOfferModal={setViewOfferModal}
          readFeedbackIds={readFeedbackIds}
          markAsHired={(applicationId: string) => {
            markAsHired(applicationId);
          }}
        />
      )}

        <RemarksPanel
        jobApplicationId={remarksPanel.jobApplicationId}
        type="recruiter-manager"
        visible={remarksPanel.visible}
        onCancel={() => setRemarksPanel({ visible: false })}
        />
      <Drawer
        title={
          <span style={{ fontSize: '20px', fontWeight: 600 }}>
            {drawerProfile?.name || drawerProfile?.profile?.firstName
              ? `${drawerProfile.name || `${drawerProfile.profile?.firstName || ''} ${drawerProfile.profile?.lastName || ''}`.trim()}'s Profile`
              : 'Candidate Profile'}
          </span>
        }
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={480}
      >
        {drawerLoading ? (
          <Spin />
        ) : drawerProfile ? (() => {
          const p = drawerProfile.profile || drawerProfile;
          const name = drawerProfile.name || `${p.firstName || ''} ${p.lastName || ''}`.trim();
          const profilePic = p.profilePhoto || p.profilePictureUrl || drawerProfile.profilePicture || drawerProfile.candidate?.profilePictureUrl || '';
          const resume = p.resume || drawerProfile.resume || '';
          const linkedin = p.linkedin || drawerProfile.linkedin || drawerProfile.candidate?.linkedin || '';
          const location = p.location || drawerProfile.location || drawerProfile.candidate?.location || '';
          const email = drawerProfile.email || p.email || '';
          return (
            <div style={{ fontSize: 15 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                {profilePic ? (
                  <img src={getProfilePhotoUrl(profilePic)} alt="Profile" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <Avatar size={72} style={{ background: '#1890ff', fontSize: 26, fontWeight: 700, flexShrink: 0 }}>
                    {(name || '?')[0].toUpperCase()}
                  </Avatar>
                )}
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{name}</div>
                  {p.positionTitle && <div style={{ color: '#666', marginTop: 2 }}>{p.positionTitle}</div>}
                  {p.yearsOfExperience != null && <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>🏅 {p.yearsOfExperience} yrs experience</div>}
                </div>
              </div>

              {/* Contact */}
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Contact Info</div>
                {email && <div style={{ marginBottom: 4 }}>✉️ {email}</div>}
                {p.phoneNumber && <div style={{ marginBottom: 4 }}>📞 {p.phoneNumber}</div>}
                {location && <div style={{ marginBottom: 4 }}>📍 {location}</div>}
                {linkedin && (
                  <a href={linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                    <LinkedinOutlined style={{ marginRight: 4 }} />LinkedIn Profile
                  </a>
                )}
              </div>

              {/* Work Preferences */}
              {(p.jobPreference || p.relocation || p.isSearching != null) && (
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Work Preferences</div>
                  {p.jobPreference && <div style={{ marginBottom: 4 }}>💼 <b>Preference:</b> {p.jobPreference}</div>}
                  {p.relocation && <div style={{ marginBottom: 4 }}>🏠 <b>Relocation:</b> {p.relocation}</div>}
                  {p.isSearching != null && (
                    <div style={{ marginBottom: 4 }}>🔍 <b>Job status:</b> {p.isSearching ? 'Actively seeking' : 'Open to opportunities'}</div>
                  )}
                </div>
              )}

              {/* Resume */}
              {resume && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Resume</div>
                  <Button type="primary" icon={<FilePdfOutlined />} href={getResumeUrl(resume)} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 10 }}>
                    Open Resume
                  </Button>
                  <iframe src={getResumeUrl(resume)} title="Resume Preview" width="100%" height="380px" style={{ border: '1px solid #eee', borderRadius: 6, display: 'block' }} />
                </div>
              )}
            </div>
          );
        })() : (
          <p style={{ fontSize: '16px' }}>No profile data available.</p>
        )}
      </Drawer>

      <Modal
        title="Reject Candidate"
        open={recruiterRejectModal.visible}
        onOk={handleRecruiterRejectConfirm}
        onCancel={() => setRecruiterRejectModal({ visible: false })}
        okText="Reject Candidate"
        okButtonProps={{ danger: true, loading: recruiterRejectLoading }}
      >
        <p style={{ marginBottom: 12, color: '#555' }}>Provide a reason for rejection (optional).</p>
        <textarea
          value={recruiterRejectReason}
          onChange={e => setRecruiterRejectReason(e.target.value)}
          placeholder="Reason for rejection (optional)"
          rows={3}
          style={{ width: '100%', resize: 'vertical', borderRadius: 6, padding: 8, border: '1px solid #d9d9d9', fontSize: 14 }}
        />
      </Modal>

      <Modal
        title={<span style={{ fontSize: '22px', fontWeight: 'bold' }}>Talent Pool</span>}
        open={talentPoolModal.visible}
        onCancel={() => setTalentPoolModal({ visible: false, note: '', saving: false, loadingNote: false })}
        footer={null}
        width={520}
      >
        {talentPoolModal.managerFeedback && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>Manager Feedback</p>
            <div style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 8, padding: '12px 14px', fontSize: 14, color: '#444', whiteSpace: 'pre-wrap' }}>
              {talentPoolModal.managerFeedback}
            </div>
          </div>
        )}
        <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>Recruiter Note</p>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>Add context for future reference — e.g. "Strong candidate, consider for senior role in 6 months."</p>
        <textarea
          value={talentPoolModal.note}
          onChange={e => setTalentPoolModal(prev => ({ ...prev, note: e.target.value }))}
          rows={5}
          disabled={talentPoolModal.loadingNote}
          placeholder={talentPoolModal.loadingNote ? 'Loading...' : 'Add a note...'}
          style={{ width: '100%', fontSize: '14px', borderRadius: 6, border: '1px solid #d9d9d9', padding: '8px 10px', resize: 'vertical' }}
        />
        <Button
          type="primary"
          loading={talentPoolModal.saving}
          disabled={talentPoolModal.loadingNote}
          style={{ marginTop: 14, fontSize: '15px' }}
          onClick={async () => {
            setTalentPoolModal(prev => ({ ...prev, saving: true }));
            try {
              await axiosInstant.patch(`/jobs/candidate-pool/note-for-user/${talentPoolModal.candidateId}`, { note: talentPoolModal.note });
              message.success('Saved to Talent Pool');
              setTalentPoolModal({ visible: false, note: '', saving: false, loadingNote: false });
            } catch {
              message.error('Failed to save note');
              setTalentPoolModal(prev => ({ ...prev, saving: false }));
            }
          }}
        >
          Save to Talent Pool
        </Button>
      </Modal>

      <Modal
        title="Place Offer"
        open={offerModal.visible}
        onCancel={() => setOfferModal({ visible: false })}
        footer={null}
      >
        <Input
          placeholder="Position Title"
          value={offerFields.positionTitle}
          onChange={e => setOfferFields({ ...offerFields, positionTitle: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Company Name"
          value={offerFields.companyName}
          onChange={e => setOfferFields({ ...offerFields, companyName: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Select
          value={offerFields.employmentType}
          onChange={val => setOfferFields({ ...offerFields, employmentType: val })}
          style={{ width: "100%", marginBottom: 8 }}
          options={[
            { value: "Full-time", label: "Full-time" },
            { value: "Part-time", label: "Part-time" }
          ]}
        />
        <Input
          placeholder="Salary"
          value={offerFields.salary}
          onChange={e => setOfferFields({ ...offerFields, salary: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Location"
          value={offerFields.location}
          onChange={e => setOfferFields({ ...offerFields, location: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <DatePicker
          value={offerFields.startDate}
          onChange={date => setOfferFields({ ...offerFields, startDate: date })}
          style={{ width: "100%", marginBottom: 8 }}
        />
        <Input.TextArea
          placeholder="Notes/Custom Terms"
          value={offerFields.notes}
          onChange={e => setOfferFields({ ...offerFields, notes: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Button
          onClick={generateOfferLetter}
          loading={generating}
          style={{ marginBottom: 8 }}
          block
        >
          Generate Offer Letter with AI
        </Button>
        <Input.TextArea
          placeholder="Offer Letter"
          value={offerFields.offerLetter}
          onChange={e => setOfferFields({ ...offerFields, offerLetter: e.target.value })}
          rows={6}
          style={{ marginBottom: 8 }}
        />
        <Button
          type="primary"
          block
          onClick={shareOfferWithCandidate}
          disabled={!canShare}
        >
          Share Offer with Candidate
        </Button>
      </Modal>

      <Modal
        title="Offer Letter"
        open={viewOfferModal.visible}
        onCancel={() => setViewOfferModal({ visible: false, record: undefined })}
        footer={null}
      >
        {viewOfferModal.record && (
          <div>
            <b>Offer Letter:</b>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{viewOfferModal.record.offerLetter}</pre>
            {viewOfferModal.record.offerAccepted && (
              <div style={{ color: "green", marginTop: 8 }}>
                Offer Accepted {viewOfferModal.record.offerAcceptedDate && `on ${new Date(viewOfferModal.record.offerAcceptedDate).toLocaleDateString()}`}
              </div>
            )}
            {viewOfferModal.record.offerRejected && (
              <div style={{ color: "red", marginTop: 8 }}>
                Offer Rejected {viewOfferModal.record.offerRejectedDate && `on ${new Date(viewOfferModal.record.offerRejectedDate).toLocaleDateString()}`}
              </div>
            )}
          </div>
        )}
      </Modal>

      
      <Modal
        title="Send Final Rejection to Candidate"
        open={sendRejectionModal.visible}
        onOk={handleSendRejection}
        onCancel={() => setSendRejectionModal({ visible: false })}
        okText="Send Rejection Email"
        okButtonProps={{ danger: true, loading: sendRejectionLoading }}
      >
        <p style={{ marginBottom: 12, color: '#555' }}>
          You are sending a final rejection email to <b>{sendRejectionModal.candidateName}</b>. Edit the message below before sending.
        </p>
        <textarea
          value={sendRejectionMessage}
          onChange={e => setSendRejectionMessage(e.target.value)}
          rows={5}
          placeholder="Enter rejection message (leave blank for a default message)"
          style={{ width: '100%', resize: 'vertical', borderRadius: 6, padding: 8, border: '1px solid #d9d9d9', fontSize: 14 }}
        />
      </Modal>

      {/* Confetti Celebration */}
      {console.log('🎊 Confetti state in render:', showConfetti)}
      <ConfettiCelebration 
        show={showConfetti} 
        onComplete={() => {
          console.log('🎊 Confetti completed, setting to false');
          setShowConfetti(false);
        }} 
        duration={5000}
      />
    </>
  );
};
export default ApplicantsListPanel;